// Single source of truth for React Query cache keys.
//
// Every useQuery / useMutation in the client must reference one of these
// keys — no inline string arrays. New entities get added here first, before
// any hook touches them.

export type QueryKey = readonly unknown[];

export type FilterRecord = Readonly<Record<string, unknown>>;

export const queryKeys = {
  dashboard: {
    /** Root key — used to invalidate every dashboard sub-query at once. */
    all: ['dashboard'] as const,
    /** Per-day rollup; bound to `?date=YYYY-MM-DD`. */
    today: (date: string) => ['dashboard', 'today', date] as const,
    /** Per-day rollup for arbitrary date (e.g. yesterday for delta). */
    forDate: (date: string) => ['dashboard', 'date', date] as const,
    /** Cancellation-requested bookings counter. */
    cancellations: ['dashboard', 'cancellations'] as const,
    /** Pending-queue counters (open + final-notice). */
    pending: ['dashboard', 'pending'] as const,
    /** Recent activity feed. */
    activity: (limit: number) => ['dashboard', 'activity', limit] as const,
  },

  diary: {
    /** Active drivers feeding the diary selector. */
    drivers: ['diary', 'drivers'] as const,
    /** Per-driver weekly view. */
    week: (driverId: number, weekStart: string) =>
      ['diary', 'week', driverId, weekStart] as const,
    /** Cross-driver "today" rollup; assembled from /bookings. */
    today: (date: string, driverId?: number | null) =>
      ['diary', 'today', date, driverId ?? null] as const,
    /** Per-(driver, day) notes. */
    notes: (driverId: number, date: string) =>
      ['diary', 'notes', driverId, date] as const,
    /** Per-(driver, week) notes — fetched from `/diary/notes` with the
     *  week_start..week_end range. Currently the canonical source of
     *  notes for `DiaryResultsPage` because the weekly diary endpoint's
     *  embedded `days[].notes` arrays are unreliable (server bug —
     *  HANDOFF gap). Mutations invalidate this key + `diary.week` so
     *  both the bookings panel and the notes section refresh. */
    weekNotes: (driverId: number, weekStart: string) =>
      ['diary', 'week-notes', driverId, weekStart] as const,
  },

  bookings: {
    all: ['bookings'] as const,
    list: (filters: FilterRecord) => ['bookings', 'list', filters] as const,
    detail: (encId: string) => ['bookings', 'detail', encId] as const,
  },

  pending: {
    all: ['pending'] as const,
    list: (filters: FilterRecord) => ['pending', 'list', filters] as const,
    detail: (encId: string) => ['pending', 'detail', encId] as const,
  },

  cancellations: {
    list: (filters: FilterRecord) => ['cancellations', 'list', filters] as const,
  },

  customers: {
    all: ['customers'] as const,
    list: (filters: FilterRecord) => ['customers', 'list', filters] as const,
    detail: (uuid: string) => ['customers', 'detail', uuid] as const,
    bookings: (uuid: string) => ['customers', 'bookings', uuid] as const,
    bankHistory: (uuid: string) => ['customers', 'bank-history', uuid] as const,
  },

  receipts: {
    all: ['receipts'] as const,
    list: (filters: FilterRecord) => ['receipts', 'list', filters] as const,
    detail: (encId: string) => ['receipts', 'detail', encId] as const,
    photos: (encId: string) => ['receipts', 'photos', encId] as const,
    unsentCount: ['receipts', 'unsent-count'] as const,
  },

  // Location Tracking — Phase 4. `latest` polls every driver's most
  // recent position; `history` is keyed by (driver, date).
  driverLocations: {
    latest: () => ['driverLocations', 'latest'] as const,
    history: (encDriverId: string, date: string) =>
      ['driverLocations', 'history', encDriverId, date] as const,
  },

  staffLocations: {
    latest: () => ['staffLocations', 'latest'] as const,
  },

  drivers: {
    /** General drivers list (settings, not diary selector). */
    list: ['drivers', 'list'] as const,
    /** Per-driver holiday probe used by the diary + book-in pages. */
    holidayCheck: (driverEncId: string, startDate: string, endDate: string) =>
      ['driver-holiday', driverEncId, startDate, endDate] as const,
  },

  referenceData: {
    products: (companyId: number | null) => ['ref', 'products', companyId] as const,
    workSources: (companyId: number | null) => ['ref', 'work-sources', companyId] as const,
    paymentModes: (companyId: number | null) => ['ref', 'payment-modes', companyId] as const,
    /** AM / AM-PM / PM per day type. Fetched from
     *  GET /api/v1/collections/time-slots?type=weekday|weekend (migration 069). */
    timeSlots: (type: 'weekday' | 'weekend', companyId: number | null) =>
      ['ref', 'time-slots', type, companyId] as const,
    /** Cancellation reason codes. Server-side cancel takes a freeform
     *  reason + a `destination` enum (`bin` | `pending`); reserved for
     *  forward compat. */
    cancellationReasons: ['ref', 'cancellation-reasons'] as const,
  },

  drafts: {
    list: (filters: FilterRecord) => ['drafts', 'list', filters] as const,
    detail: (encId: string) => ['drafts', 'detail', encId] as const,
  },

  activityLog: {
    list: (filters: FilterRecord) => ['activity-log', 'list', filters] as const,
  },

  notifications: {
    all: ['notifications'] as const,
    queue: (filters: { status: string; type: string; page: number; limit: number }) =>
      ['notifications', 'queue', filters] as const,
    summary: ['notifications', 'summary'] as const,
  },

  reports: {
    all: ['reports'] as const,
    list: ['reports', 'list'] as const,
    /** Reminders tab — keyed by selected date. */
    reminders: (date: string) => ['reports', 'reminders', date] as const,
    run: (key: string, params: FilterRecord) =>
      ['reports', 'run', key, params] as const,
  },

  // -- Wave D ---------------------------------------------------------------
  invoices: {
    all: ['invoices'] as const,
    list: (filters: FilterRecord) => ['invoices', 'list', filters] as const,
    detail: (encId: string) => ['invoices', 'detail', encId] as const,
    workSources: ['invoices', 'work-sources'] as const,
  },

  payments: {
    all: ['payments'] as const,
  },

  bacs: {
    all: ['bacs'] as const,
    list: (filters: FilterRecord) => ['bacs', 'list', filters] as const,
    unpaid: (filters: FilterRecord) => ['bacs', 'unpaid', filters] as const,
    paid: (filters: FilterRecord) => ['bacs', 'paid', filters] as const,
    detail: (encId: string) => ['bacs', 'detail', encId] as const,
  },

  quickbooks: {
    all: ['quickbooks'] as const,
    // status / settings / syncLog are module-scoped (collections | weighbridge
    // | skip) so switching the module selector refetches a distinct cache.
    status: (module: string) => ['quickbooks', 'status', module] as const,
    syncLog: (module: string, filters: FilterRecord) =>
      ['quickbooks', 'sync-log', module, filters] as const,
    mappings: (filters: FilterRecord) => ['quickbooks', 'mappings', filters] as const,
    eligible: () => ['quickbooks', 'eligible'] as const,
    records: (filters: FilterRecord) => ['quickbooks', 'records', filters] as const,
    settings: (module: string) => ['quickbooks', 'settings', module] as const,
    accounts: (module: string) => ['quickbooks', 'accounts', module] as const,
  },

  // -- Wave E ---------------------------------------------------------------
  users: {
    all: ['users'] as const,
    list: (filters: FilterRecord) => ['users', 'list', filters] as const,
    detail: (uuid: string) => ['users', 'detail', uuid] as const,
  },

  holidays: {
    all: ['holidays'] as const,
    list: (filters: FilterRecord) => ['holidays', 'list', filters] as const,
  },

  driversAdmin: {
    all: ['drivers-admin'] as const,
    list: (filters: FilterRecord) => ['drivers-admin', 'list', filters] as const,
    detail: (uuid: string) => ['drivers-admin', 'detail', uuid] as const,
    /** Active drivers feeding the map history-replay selector. */
    activeForMap: () => ['drivers-admin', 'active-for-map'] as const,
  },

  templates: {
    all: ['templates'] as const,
    list: (filters: FilterRecord) => ['templates', 'list', filters] as const,
    detail: (uuid: string) => ['templates', 'detail', uuid] as const,
  },

  company: {
    all: ['company'] as const,
    overview: ['company', 'overview'] as const,
    settings: ['company', 'settings'] as const,
    theme: ['company', 'theme'] as const,
    // Geocoded map centre derived from the company postcode. Kept distinct
    // from `overview` so it can cache a [lat, lng] tuple without colliding
    // with the CompanyOverviewResponse cached under `overview`.
    mapCentre: ['company', 'map-centre'] as const,
  },

  collectionsSettings: {
    all: ['collections-settings'] as const,
  },

  superStorage: {
    all: (encId: string) => ['super', 'storage', encId] as const,
    config: (encId: string) => ['super', 'storage', encId, 'config'] as const,
  },

  communication: {
    all: ['communication'] as const,
    email: (purpose: string) => ['communication', 'email', purpose] as const,
    emailList: ['communication', 'email-list'] as const,
    sms: () => ['communication', 'sms'] as const,
    preferences: () => ['communication', 'preferences'] as const,
    parsers: () => ['communication', 'parsers'] as const,
  },

  products: {
    all: ['products'] as const,
    list: ['products', 'list'] as const,
  },

  workSources: {
    all: ['workSources'] as const,
    list: ['workSources', 'list'] as const,
  },

  paymentModes: {
    all: ['paymentModes'] as const,
    list: ['paymentModes', 'list'] as const,
  },

  timeSlots: {
    all: ['timeSlots'] as const,
    list: (type: 'weekday' | 'weekend') => ['timeSlots', 'list', type] as const,
  },

  // -- Stage 5 — Skip Management module ------------------------------------
  skipJobs: {
    all: ['skip', 'jobs'] as const,
    list: (filters: FilterRecord) => ['skip', 'jobs', 'list', filters] as const,
    detail: (encId: string) => ['skip', 'jobs', 'detail', encId] as const,
    children: (parentUuid: string) => ['skip', 'jobs', 'children', parentUuid] as const,
    notes: (encId: string) => ['skip', 'jobs', 'notes', encId] as const,
    attachments: (encId: string) => ['skip', 'jobs', 'attachments', encId] as const,
    /** Active-actions list — jobs with any sub-flag in 'requested' state. */
    activeActions: (filters: FilterRecord) => ['skip', 'jobs', 'active-actions', filters] as const,
    /** Available actions for ONE job — drives the detail page button row. */
    availableActions: (encId: string) =>
      ['skip', 'jobs', 'available-actions', encId] as const,
  },
  skipEnquiries: {
    all: ['skip', 'enquiries'] as const,
    list: (filters: FilterRecord) => ['skip', 'enquiries', 'list', filters] as const,
    detail: (encId: string) => ['skip', 'enquiries', 'detail', encId] as const,
    contacts: (encId: string) => ['skip', 'enquiries', 'contacts', encId] as const,
    /** Feature 2 — pending sub-flag requests (jobs with any
     *  collection/exchange/tip_return sub-flag = 'requested'). */
    pendingRequests: (filters: FilterRecord) =>
      ['skip', 'enquiries', 'pending-requests', filters] as const,
    /** Skip Module v2 — enquiry attachments list. Invalidated after
     *  upload / delete. */
    attachments: (encId: string) =>
      ['skip', 'enquiries', 'attachments', encId] as const,
  },
  skipInventory: {
    all: ['skip', 'inventory'] as const,
    list: (filters: FilterRecord) => ['skip', 'inventory', 'list', filters] as const,
    detail: (encId: string) => ['skip', 'inventory', 'detail', encId] as const,
    history: (encId: string) => ['skip', 'inventory', 'history', encId] as const,
  },
  skipCustomers: {
    all: ['skip', 'customers'] as const,
    list: (filters: FilterRecord) => ['skip', 'customers', 'list', filters] as const,
    detail: (encId: string) => ['skip', 'customers', 'detail', encId] as const,
    jobs: (encId: string) => ['skip', 'customers', 'jobs', encId] as const,
  },
  skipSizes: {
    list: ['skip', 'sizes'] as const,
  },
  skipStockTypes: {
    all: ['skip', 'stock', 'types'] as const,
    list: (opts: { forSelection?: boolean } = {}) =>
      ['skip', 'stock', 'types', { forSelection: opts.forSelection ?? false }] as const,
    detail: (encId: string) => ['skip', 'stock', 'type', encId] as const,
    sizes: (typeEncId: string) =>
      ['skip', 'stock', 'sizes', typeEncId] as const,
  },
  skipJobTypes: {
    all: ['skip', 'job-types'] as const,
    list: (opts: { forSelection?: boolean } = {}) =>
      ['skip', 'job-types', 'list', { forSelection: opts.forSelection ?? false }] as const,
    detail: (encId: string) => ['skip', 'job-types', 'detail', encId] as const,
    stages: (typeEncId: string) =>
      ['skip', 'job-types', 'stages', typeEncId] as const,
    actions: (typeEncId: string) =>
      ['skip', 'job-types', 'actions', typeEncId] as const,
  },
  customerSites: {
    list: (customerEncId: string) => ['customers', 'sites', customerEncId] as const,
  },

  // -- Stage 6 — Weighbridge Management module ----------------------------
  // C1 scaffold: per-resource list keys + a couple of common detail/filter
  // shapes. Phase prompts extend this namespace with their own
  // sub-resource keys (lines / calibrations / weight-events / etc).
  wb: {
    all: ['wb'] as const,
    yards: (filters: FilterRecord = {}) => ['wb', 'yards', filters] as const,
    yard: (encId: string) => ['wb', 'yard', encId] as const,
    weighbridges: (filters: FilterRecord = {}) =>
      ['wb', 'weighbridges', filters] as const,
    weighbridge: (encId: string) => ['wb', 'weighbridge', encId] as const,
    liveBridges: ['wb', 'live', 'bridges'] as const,
    calibrations: (weighbridgeEncId: string) =>
      ['wb', 'calibrations', weighbridgeEncId] as const,
    cameras: (filters: FilterRecord = {}) => ['wb', 'cameras', filters] as const,
    camera: (encId: string) => ['wb', 'camera', encId] as const,
    barriers: (filters: FilterRecord = {}) => ['wb', 'barriers', filters] as const,
    barrier: (encId: string) => ['wb', 'barrier', encId] as const,
    agents: (filters: FilterRecord = {}) => ['wb', 'agents', filters] as const,
    agent: (encId: string) => ['wb', 'agent', encId] as const,
    hauliers: (filters: FilterRecord = {}) => ['wb', 'hauliers', filters] as const,
    haulier: (encId: string) => ['wb', 'haulier', encId] as const,
    vehicles: (filters: FilterRecord = {}) => ['wb', 'vehicles', filters] as const,
    vehicle: (encId: string) => ['wb', 'vehicle', encId] as const,
    vehiclesSearch: (q: string) => ['wb', 'vehicles', 'search', q] as const,
    drivers: (filters: FilterRecord = {}) => ['wb', 'drivers', filters] as const,
    driver: (encId: string) => ['wb', 'driver', encId] as const,
    products: (filters: FilterRecord = {}) => ['wb', 'products', filters] as const,
    product: (encId: string) => ['wb', 'product', encId] as const,
    productGroups: ['wb', 'product-groups'] as const,
    vatRates: ['wb', 'vat-rates'] as const,
    companySettings: ['wb', 'company-settings'] as const,
    customers: (filters: FilterRecord = {}) =>
      ['wb', 'customers', filters] as const,
    customerSettings: (customerEncId: string) =>
      ['wb', 'customer-settings', customerEncId] as const,
    priceBands: (filters: FilterRecord = {}) =>
      ['wb', 'price-bands', filters] as const,
    priceBand: (encId: string) => ['wb', 'price-band', encId] as const,
    priceBandLines: (encId: string) =>
      ['wb', 'price-band', encId, 'lines'] as const,
    contracts: (filters: FilterRecord = {}) =>
      ['wb', 'contracts', filters] as const,
    contract: (encId: string) => ['wb', 'contract', encId] as const,
    contractLines: (encId: string) => ['wb', 'contract', encId, 'lines'] as const,
    tickets: (filters: FilterRecord = {}) => ['wb', 'tickets', filters] as const,
    ticket: (encId: string) => ['wb', 'ticket', encId] as const,
    ticketsAwaitingSecond: ['wb', 'tickets', 'awaiting-second-weigh'] as const,
    stockBalances: (filters: FilterRecord = {}) =>
      ['wb', 'stock', 'balances', filters] as const,
    stockMovements: (filters: FilterRecord = {}) =>
      ['wb', 'stock', 'movements', filters] as const,
    stockAlerts: (filters: FilterRecord = {}) =>
      ['wb', 'stock-alerts', filters] as const,
    cameraConfig: (weighbridgeEncId: string) =>
      ['wb', 'camera-config', weighbridgeEncId] as const,
    anprLog: (weighbridgeEncId: string) =>
      ['wb', 'anpr-log', weighbridgeEncId] as const,
  },

  // Print templates (PRINT_TEMPLATE_PLAN.md §6.3). 5-min staleTime —
  // templates change rarely, and a stale layout in the settings list
  // is less harmful than the constant refetch a 0 ms staleTime would
  // produce while moving between settings sub-pages. Render requests
  // are NOT cached (they're mutations — fresh PDF every call).
  printTemplates: {
    all: ['printTemplates'] as const,
    list: ['printTemplates', 'list'] as const,
    detail: (type: string) => ['printTemplates', 'detail', type] as const,
  },

  // GeoLink — Nearby Bookings + Coverage Analysis surfaces.
  geolink: {
    all: ['geolink'] as const,
    nearby: (filters: FilterRecord) => ['geolink', 'nearby', filters] as const,
    coverage: (filters: FilterRecord) => ['geolink', 'coverage', filters] as const,
  },
} as const;
